package songwritersnotebook.app.components

import androidx.compose.foundation.layout.*
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Videocam
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.delay
import kotlinx.datetime.*
import songwritersnotebook.app.data.UserProfile

/**
 * PRO UI: A specialized button for rewarded ads that handles its own 
 * countdown timer and daily limit display logic.
 */
@Composable
fun AdRewardButton(
    userProfile: UserProfile?,
    modifier: Modifier = Modifier,
    onClick: () -> Unit
) {
    val now = remember { mutableStateOf(kotlinx.datetime.Clock.System.now().toEpochMilliseconds()) }
    
    // Update 'now' every second to keep the countdown ticking
    LaunchedEffect(Unit) {
        while(true) {
            delay(1000)
            now.value = kotlinx.datetime.Clock.System.now().toEpochMilliseconds()
        }
    }

    val COOLDOWN_MS = 10 * 60 * 1000L
    val DAILY_LIMIT = 3
    
    // Calculate effective daily count based on date
    val today = remember(now.value) { 
        val instant = kotlinx.datetime.Instant.fromEpochMilliseconds(now.value)
        instant.toString().split('T')[0] 
    }
    
    val currentDailyCount = if (userProfile?.lastAdDate == today) userProfile.dailyAdCount else 0
    val lastAwardedAt = userProfile?.lastAdAwardedAt?.toLong() ?: 0L
    val timeSinceLast = now.value - lastAwardedAt
    
    val isInCooldown = timeSinceLast < COOLDOWN_MS && lastAwardedAt > 0
    val isAtLimit = currentDailyCount >= DAILY_LIMIT

    val remainingCooldownSecs = if (isInCooldown) ((COOLDOWN_MS - timeSinceLast) / 1000).toInt() else 0
    
    val buttonText = when {
        isAtLimit -> {
            val instant = kotlinx.datetime.Instant.fromEpochMilliseconds(now.value)
            val timeZone = kotlinx.datetime.TimeZone.currentSystemDefault()
            val localNow = instant.toLocalDateTime(timeZone)
            val tomorrow = instant.plus(1, kotlinx.datetime.DateTimeUnit.DAY, timeZone).toLocalDateTime(timeZone).date
            val resetTime = tomorrow.atStartOfDayIn(timeZone)
            val diff = resetTime - instant
            val hours = diff.inWholeHours
            val mins = diff.inWholeMinutes % 60
            "Limit Reached (Resets in ${hours}h ${mins}m)"
        }
        isInCooldown -> {
            val mins = remainingCooldownSecs / 60
            val secs = remainingCooldownSecs % 60
            "Wait: ${mins}:${secs.toString().padStart(2, '0')}"
        }
        else -> "Watch Ad (+5)"
    }

    val isEnabled = !isAtLimit && !isInCooldown

    TextButton(
        onClick = onClick,
        enabled = isEnabled,
        modifier = modifier
    ) {
        Icon(Icons.Default.Videocam, null, modifier = Modifier.size(16.dp))
        Spacer(Modifier.width(4.dp))
        Text(buttonText, style = MaterialTheme.typography.labelSmall)
    }
}
